// House style for any text the app shows, including text that came from a model.
//
// Rule: no em dashes (U+2014). Static copy is checked by src/domain/__tests__/wellness.test.ts;
// dynamic text (model labels, user-entered notes rendered back) goes through cleanText().

const EM_DASH = /\s*\u2014\s*/g;

/** Replace em dashes with a comma pause and collapse stray whitespace. */
export function cleanText(s: string): string {
  return s
    .replace(EM_DASH, ', ')
    .replace(/\s+,/g, ',')
    .replace(/,\s*([.!?])/g, '$1')
    .replace(/^,\s*/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Longest text accepted in any search box. */
export const MAX_QUERY = 80;

// C0/C1 controls, zero-width characters and bidi overrides: invisible, and used to smuggle hidden
// or reversed text. The same set is stripped from model output in the scan function.
// eslint-disable-next-line no-control-regex -- matching control characters is the point
const INVISIBLE = /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2060-\u2064\ufeff]/g;

/**
 * Typed text, made safe to keep and show: normalised (NFC), invisible characters removed,
 * whitespace runs collapsed, capped at `max`. Leading and inner spaces are kept so typing feels
 * normal; callers trim when they save. React escapes everything it renders, so this is about
 * hidden characters and size, not HTML.
 */
export function sanitizeInput(s: string, max: number): string {
  return s.normalize('NFC').replace(INVISIBLE, ' ').replace(/\s{2,}/g, ' ').slice(0, max);
}

export function hasEmDash(s: string): boolean {
  return s.includes('\u2014');
}

/** "a", "a and b", "a, b and c". */
export function joinNatural(items: string[], conj = 'and'): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} ${conj} ${items[items.length - 1]}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
